import { useEffect, useMemo, useState } from 'react'
import { formatClock, formatDuration } from '../lib/format'
import { parseTags } from '../data/tags'
import { instantAtClock } from '../lib/time'
import type { Block, Category } from '../data/types'

export interface BlockEdit {
  categoryId: string
  note: string | null
  tags: string[]
  /** Only present when the times were changed and are valid. */
  bounds?: { start: number; end: number | null }
}

interface Props {
  block: Block
  categories: Category[]
  /** The free stretch the block may occupy, bounded by its neighbours. */
  limits: { from: number; to: number }
  /** Tags already in use, for autocomplete. */
  knownTags?: string[]
  onSave: (edit: BlockEdit) => void
  onDelete: () => void
  onClose: () => void
}

/** Centred dialog on a desktop, bottom sheet on a phone. */
export function BlockEditor({
  block,
  categories,
  limits,
  knownTags = [],
  onSave,
  onDelete,
  onClose,
}: Props) {
  const [categoryId, setCategoryId] = useState(block.categoryId)
  const [note, setNote] = useState(block.note ?? '')
  const [tags, setTags] = useState(block.tags.join(', '))
  const [startText, setStartText] = useState(() => toTimeValue(block.start))
  const [endText, setEndText] = useState(() => (block.end === null ? '' : toTimeValue(block.end)))
  const [minutes, setMinutes] = useState(() =>
    block.end === null ? '' : String(Math.round((block.end - block.start) / 60_000)),
  )
  /**
   * The times are only validated once they have been touched. A block stored to
   * the second reads back rounded to the minute, so re-parsing an untouched
   * field can look like it overlaps a neighbour it is actually flush against —
   * which would block a save that never meant to move it at all.
   */
  const [retimed, setRetimed] = useState(false)

  // Times are parsed against the block's own day, so a value that crosses
  // midnight lands on the next day rather than jumping backwards.
  const parsed = useMemo(() => {
    if (!retimed) return { start: block.start, end: block.end, untouched: true } as const
    const start = fromTimeValue(startText, block.start)
    if (start === null) return { error: 'That start time is not a time.' } as const
    if (block.end === null) {
      if (start > Date.now()) return { error: 'A running block cannot start in the future.' } as const
      if (start < limits.from) return { error: 'That overlaps the block before it.' } as const
      return { start, end: null } as const
    }
    const end = fromTimeValue(endText, start, true)
    if (end === null) return { error: 'That end time is not a time.' } as const
    if (end <= start) return { error: 'It has to end after it starts.' } as const
    if (start < limits.from) return { error: 'That overlaps the block before it.' } as const
    if (end > limits.to) return { error: 'That overlaps the block after it.' } as const
    return { start, end } as const
  }, [retimed, startText, endText, block.start, block.end, limits])

  const invalid = 'error' in parsed ? parsed.error : null

  function setDuration(value: string) {
    setRetimed(true)
    setMinutes(value)
    const count = Number(value)
    if (!Number.isFinite(count) || count <= 0) return
    const start = fromTimeValue(startText, block.start)
    if (start === null) return
    setEndText(toTimeValue(start + count * 60_000))
  }

  function syncMinutesFrom(start: string, end: string) {
    const from = fromTimeValue(start, block.start)
    const to = fromTimeValue(end, from ?? block.start, true)
    if (from === null || to === null || to <= from) return
    setMinutes(String(Math.round((to - from) / 60_000)))
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function save() {
    if (invalid) return
    const bounds =
      'error' in parsed || 'untouched' in parsed
        ? undefined
        : { start: parsed.start, end: parsed.end }
    onSave({
      categoryId,
      note: note.trim() || null,
      tags: parseTags(tags),
      ...(bounds ? { bounds } : {}),
    })
  }

  return (
    // A misclick on the backdrop does not throw the edit away: Cancel, Escape or
    // Save are the only ways out.
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit block"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
    >
      <div className="pb-safe w-full max-w-md rounded-t-2xl border border-line bg-surface p-4 pb-6 sm:rounded-2xl sm:pb-5">
        <header className="flex items-baseline justify-between pb-3">
          <h2 className="text-[15px] font-semibold">Edit block</h2>
          <p className="text-[13px] text-faint tnum">
            {formatClock(new Date(block.start))}
            {block.end ? `–${formatClock(new Date(block.end))} · ${formatDuration(block.end - block.start)}` : ' · running'}
          </p>
        </header>

        <div className="flex flex-wrap gap-1.5 pb-3">
          {categories.map((category) => {
            const selected = category.id === categoryId
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => setCategoryId(category.id)}
                aria-pressed={selected}
                style={{ '--c': category.color } as React.CSSProperties}
                className={`rounded-md border px-2 py-1 text-[13px] leading-none font-medium transition-colors duration-150 ${
                  selected
                    ? 'border-[var(--c)] bg-[var(--c)] text-[#0a0b0c]'
                    : 'border-[color-mix(in_oklab,var(--c)_34%,transparent)] bg-[color-mix(in_oklab,var(--c)_12%,var(--tg-surface))] text-[color-mix(in_oklab,var(--c)_72%,var(--tg-text))]'
                }`}
              >
                {category.shortLabel}
              </button>
            )
          })}
        </div>


        <div className="flex flex-wrap items-end gap-2 pb-2.5">
          <Field label="From">
            <input
              type="time"
              value={startText}
              onChange={(event) => {
                setRetimed(true)
                setStartText(event.target.value)
                syncMinutesFrom(event.target.value, endText)
              }}
              className="rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] tnum outline-none focus:border-line-strong"
            />
          </Field>
          {block.end !== null ? (
            <>
              <Field label="To">
                <input
                  type="time"
                  value={endText}
                  onChange={(event) => {
                    setRetimed(true)
                    setEndText(event.target.value)
                    syncMinutesFrom(startText, event.target.value)
                  }}
                  className="rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] tnum outline-none focus:border-line-strong"
                />
              </Field>
              <Field label="Minutes">
                <input
                  type="number"
                  min={1}
                  value={minutes}
                  onChange={(event) => setDuration(event.target.value)}
                  className="w-20 rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] tnum outline-none focus:border-line-strong"
                />
              </Field>
            </>
          ) : (
            <span className="pb-2 text-[12px] text-faint">still running</span>
          )}
        </div>

        {invalid ? <p className="pb-2 text-[13px] text-danger">{invalid}</p> : null}

        <Field label="Note">
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            className="w-full resize-none rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] outline-none focus:border-line-strong"
          />
        </Field>

        <Field label="Tags" hint="comma separated">
          <input
            value={tags}
            list="known-tags"
            onChange={(event) => setTags(event.target.value)}
            placeholder="deep-work, pairing"
            className="w-full rounded-lg border border-line bg-bg px-2.5 py-2 text-[14px] outline-none focus:border-line-strong"
          />
          <datalist id="known-tags">
            {knownTags.map((known) => (
              <option key={known} value={known} />
            ))}
          </datalist>
        </Field>

        <footer className="flex items-center gap-2 pt-4 pb-2">
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg border border-danger/40 px-3 py-2 text-[14px] font-medium text-danger transition-colors duration-150 hover:bg-danger/10"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-lg border border-line px-3 py-2 text-[14px] font-medium text-dim transition-colors duration-150 hover:text-text"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={invalid !== null}
            className="rounded-lg bg-text px-3 py-2 text-[14px] font-semibold text-bg transition-transform duration-150 active:scale-[0.98] disabled:opacity-35"
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block pb-2.5">
      <span className="flex items-baseline gap-2 pb-1">
        <span className="text-[12px] font-medium tracking-wide text-faint uppercase">{label}</span>
        {hint ? <span className="text-[11px] text-faint">{hint}</span> : null}
      </span>
      {children}
    </label>
  )
}

function toTimeValue(instant: number): string {
  return formatClock(new Date(instant))
}

/**
 * Reads "HH:mm" against the day the reference instant falls on, in the app's own
 * zone rather than the browser's. When `forward` is set, a value earlier than the
 * reference is taken as the next day — which is what makes a block that runs past
 * midnight editable. The next day is resolved from its own date, so a DST night
 * shifts by 23 or 25 hours rather than a flat 24.
 */
function fromTimeValue(value: string, reference: number, forward = false): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  const result = instantAtClock(reference, hours, minutes)
  if (forward && result <= reference) {
    return instantAtClock(reference + 24 * 60 * 60 * 1000, hours, minutes)
  }
  return result
}
